import { all, call, delay, put, race, take } from 'redux-saga/effects';
import * as _ from 'lodash';
import { createComparison, getComparison, getRun } from '../simulation.backend';
import { getRulesByCategory } from '../../../modify-rules/redux/rules.backend';
import { getRiskConfig } from '../../../risk-assessment/redux/risk/risks.backend';
import {
    fetchComparison,
    fetchComparisonFail,
    fetchComparisonRunsGaveUp,
    fetchComparisonRunsRetry,
    fetchComparisonRunsSuccess,
    fetchComparisonSuccess,
    fetchWorkspaceConfigFail,
    fetchWorkspaceConfigSuccess,
    startComparisonFail,
    startComparisonSuccess
} from './actions';
import { STOP_FOLLOWING_COMPARISON } from './actionTypes';
import {
    makeErrorPayload,
    makeSuccessPayload
} from '../../../../../shared/helpers/payload.helper';
import {
    setErrorToast,
    setSuccessToast
} from '../../../../../shared/toast/redux/actions';
import { redirectTo } from '../../../../../routing/redirectService';
import {
    DEFAULT_COMPARISON_PLAYER_LIMIT,
    getRunStatusLabel,
    isTerminalStatus,
    getPollDelay,
    MAX_CONSECUTIVE_POLL_FAILURES,
    RiskCategory,
    riskCategoryToRuleCategory,
    RunStatus,
    RUN_POLL_INTERVAL_MS
} from '../../simulation.model';
import { fetchSimulationLimits } from '../runs/actions';
import { SimulationPath } from '../../simulation.paths';

/* More rules than a workspace has, so that all of them come in one page */
const RULES_PAGE_SIZE = 100;

export function* fetchWorkspaceConfigSaga(action) {
    const { workspace } = action.payload;

    try {
        const categories = _.values(RiskCategory);
        const [rulesResponse, ...riskResponses] = yield all([
            call(getRulesByCategory, {
                jurisdiction: workspace,
                pageSize: RULES_PAGE_SIZE
            }),
            ...categories.map(category =>
                call(
                    getRiskConfig,
                    riskCategoryToRuleCategory(category),
                    workspace
                )
            )
        ]);

        yield put(
            fetchWorkspaceConfigSuccess({
                workspace,
                rules: rulesResponse.data,
                risk: _.zipObject(
                    categories,
                    riskResponses.map(response => response.data)
                )
            })
        );
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchWorkspaceConfigFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* startComparisonSaga(action) {
    try {
        const response = yield call(createComparison, action.payload);
        const { baselineRunId, variantRunId } = response.data;

        yield put(startComparisonSuccess(response.data));
        yield put(
            setSuccessToast(
                makeSuccessPayload(
                    'The baseline and the variant were queued. They are played one after the other.'
                )
            )
        );
        yield call(
            redirectTo,
            SimulationPath.comparison(baselineRunId, variantRunId)
        );
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(startComparisonFail(formattedError));
        yield put(setErrorToast(formattedError));
        // A refusal for a limit or a kill switch is about what the limits are now
        yield put(fetchSimulationLimits());
    }
}

/* What went wrong with a pair of runs that are over and are not both completed, or null when they are */
export const explainIncompleteRuns = (baseline, variant) => {
    const problems = _.compact(
        _.map({ baseline, variant }, (run, which) =>
            run.status === RunStatus.COMPLETED
                ? null
                : `The ${which} run is ${_.toLower(
                      getRunStatusLabel(run.status)
                  )}${run.error ? ': ' + run.error : ''}.`
        )
    );

    return problems.length
        ? problems.join(' ') + ' Only completed runs can be compared.'
        : null;
};

/*
  Reads the two runs of a comparison every few seconds until both are over; then, if both completed, reads the comparison. The
  baseline is played first, so the variant may well be queued for a while.

  Like the follow of a run, it takes a read that fails for a moment as the backend restarting (it does on every deploy): what was last
  read stays on the screen, which is told that it is trying again (no message for each read), the wait grows with each failure, and
  after several in a row it gives up, which the screen shows with a way to try again. It says nothing else, and never makes the
  comparison fail for it. A run that does not exist (404) is given up on at once, and says so.
*/
export function* followComparisonSaga(action) {
    const { baselineRunId, variantRunId } = action.payload;
    let failures = 0;

    while (true) {
        try {
            const [baselineResponse, variantResponse] = yield all([
                call(getRun, baselineRunId),
                call(getRun, variantRunId)
            ]);
            failures = 0;
            const baseline = baselineResponse.data;
            const variant = variantResponse.data;
            yield put(fetchComparisonRunsSuccess({ baseline, variant }));

            if (
                isTerminalStatus(baseline.status) &&
                isTerminalStatus(variant.status)
            ) {
                const problem = explainIncompleteRuns(baseline, variant);

                if (problem) {
                    yield put(fetchComparisonFail({ message: problem }));
                } else {
                    yield put(
                        fetchComparison({
                            baselineRunId,
                            variantRunId,
                            playerLimit: DEFAULT_COMPARISON_PLAYER_LIMIT
                        })
                    );
                }
                return;
            }
        } catch (error) {
            const formattedError = makeErrorPayload(error);
            failures += 1;

            if (formattedError.statusCode === 404) {
                yield put(fetchComparisonFail(formattedError));
                return;
            }
            if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                yield put(fetchComparisonRunsGaveUp());
                return;
            }
            yield put(fetchComparisonRunsRetry());
        }

        yield delay(getPollDelay(failures, RUN_POLL_INTERVAL_MS));
    }
}

/* Follows the runs until the screen that shows them is left. */
export function* watchFollowComparisonSaga(action) {
    yield race({
        follow: call(followComparisonSaga, action),
        stop: take(STOP_FOLLOWING_COMPARISON)
    });
}

export function* fetchComparisonSaga(action) {
    try {
        const response = yield call(getComparison, action.payload);
        yield put(fetchComparisonSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchComparisonFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}
